// @flow
import React, { useEffect } from "react";
import InfiniteScroll from "react-infinite-scroller";
import CarSelectorContainer from "../../container/carSelector/CarSelectionBarContainer";
import type { translate } from "../../i18next";
import type { Product } from "../../models/Product";
import type { Vehicle } from "../../models/Vehicle";
import type { ConfigReducerState } from "../../reducers/configReducer";
import "./Search.scss";
import EventEmitter from "../../helper/eventEmitter";
import GridElementContainer from "../../container/gridPages/GridElementContainer";
import Loading from "../layout/Loading";

type SearchProps = {
	products: Product[],
	newProducts: number,
	getNextProducts: () => void,
	setSearch: string => void,
	config: ConfigReducerState,
	t: translate,
	lastSearch: string,
	totalItems: number,
	currentPage: number,
	totalPages: number,
	isLoading: boolean,
	hasMore: boolean,
	vehicleSeries: Vehicle[],
	resetVehicle: () => void,
	selectedVehicle: string,
	setVehicle: (string, string) => void,
	showVehicleTip: boolean,
};

const Search = (props: SearchProps) => {
	const {
		getNextProducts,
		products,
		resetVehicle,
		setSearch,
		setVehicle,
		t,
		lastSearch,
		totalItems,
	} = props;
	const allProductsLoaded = products.length >= totalItems;
	// While the first page of a search is in flight the store still holds the
	// previous result count, which must not be presented as this search's
	// result (#56526).
	const isSearching = props.isLoading && products.length === 0;
	const noResults = !props.isLoading && totalItems === 0;
	const title = t("search.foundProducts", {
		totalItems: totalItems,
		searchPhrase: lastSearch === "undefined" ? "" : lastSearch
	});

	const loadMoreProducts = () => {
		if (props.hasMore) {
			props.getNextProducts();
		}
	};

	// Only a changed search phrase starts a request. The result count must not
	// be a dependency here: every answer with a new count would otherwise reset
	// the grid and fetch the first page a second time (#56526).
	useEffect(() => {
		new EventEmitter().emitEvent("HPM_PAGE_LOADED");
		setSearch(lastSearch);
	}, [lastSearch, setSearch]);

	const renderResults = () => (
		<React.Fragment>
			<div
				className="hpm-searchTitle"
				dangerouslySetInnerHTML={{__html: title}}
			/>
			<div className="hpm-gridList">
				<InfiniteScroll
					initialLoad={false}
					dataLength={totalItems}
					pageStart={0}
					loadMore={getNextProducts}
					hasMore={false}
					loader={<React.Fragment/>}
					threshold={250}
				>
					{products.map(product => {
						return (
							<GridElementContainer key={product.id} product={product}
							/>
						);
					})}
				</InfiniteScroll>
				{allProductsLoaded && (
					<div className="total-items">
						{`${props.totalItems} ${props.t("category.allProductsLoaded")}`}
					</div>
				)}
				{!allProductsLoaded && (
					<button type={"button"} className={"show-more-products"} onClick={loadMoreProducts}>
						{props.t("category.showAll")}
					</button>
				)}
			</div>
		</React.Fragment>
	);

	const renderNoResults = () => (
		<div className="hpm-searchNoResults">
			<div className="hpm-searchNoResults-title">{t("search.noResults")}</div>
			<div className="hpm-searchNoResults-tipsTitle">{t("search.tipsTitle")}</div>
			<ul className="hpm-searchNoResults-tips">
				{props.showVehicleTip && <li>{t("search.tipUseVehicleSelector")}</li>}
				<li>{t("search.tipSpelling")}</li>
				<li>{t("search.tipOtherTerms")}</li>
			</ul>
		</div>
	);

	const renderSearching = () => (
		<div className="hpm-searchLoading" role="status" aria-live="polite">
			<Loading height={80}/>
			<div className="hpm-searchLoading-text">{t("search.searching")}</div>
		</div>
	);

	return (
		<div className="hpm-searchPage">
			<div className="hpm-carContainer">
				<CarSelectorContainer
					resetVehicle={resetVehicle}
					setVehicle={setVehicle}
				/>
			</div>
			<div className="hpm-categoryContainer">
				{isSearching ? renderSearching() : (noResults ? renderNoResults() : renderResults())}
			</div>
		</div>
	);
};

export default Search;
